import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppleSignInButton,
  AuthFormStack,
  GoogleSignInButton,
  PasswordInput,
  useIsAppleLoginEnabled,
} from '@/components/auth';
import { useCurrentUserContext } from '@/contexts';
import { Sentry } from '@/lib';
import { AuthAction } from '@/types/gql.generated';
import { MotionFlex, type MotionFlexProps } from '@/ui';
import {
  Flex,
  Button,
  Divider,
  FormControl,
  Input,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  forwardRef,
  Box,
  Link,
  FormLabel,
  Spinner,
  Text,
} from '@/ui';
import { isValidEmail } from '@/utils/validation';
import { AuthError } from '../AuthError';
import { EmailCodeStep } from './EmailCodeStep';
import { AuthHeaderRenderer } from './hooks/AuthHeaderRenderer';
import { useAuthAction } from './hooks/useAuthAction';
import { useAuthenticate } from './hooks/useAuthenticate';
import { useCodeResendCooldown } from './hooks/useCodeResendCooldown';
import { useEmailLoginCode } from './hooks/useEmailLoginCode';
import { useSetInitialName } from './hooks/useSetInitialName';
import { ResetPasswordLink } from './ResetPasswordLink';
import { Terms } from './Terms';
import {
  AUTH_NAME_STEP as NAME_STEP,
  type AuthResult,
  type AuthHeaderRenderProp,
  type AuthPanelStep,
  type AuthFormValues as FormValues,
} from './types';
import type { AuthMutationData } from './utils/getAuthenticatedUser';

type GoogleProps = {
  googleOnly?: boolean;
  authorizeCalendar?: boolean;
};

type Props = {
  redirectTo?: string;
  renderHeader?: AuthHeaderRenderProp;
  defaults?: { name?: string; email?: string };
  googleOptions?: GoogleProps;
  fullWidth?: boolean;
  hideLogo?: boolean;
  onAuthenticated?: (
    result: AuthResult,
    data: AuthMutationData | undefined
  ) => void;
  /**
   * Fires whenever the panel moves between steps. Consumers that render their own
   * chrome around the panel need this to stay consistent with it - a fixed
   * "Log in or create an account" title sitting above the name step contradicts
   * the panel, since by then the user is already logged in.
   */
  onStepChange?: (step: AuthPanelStep) => void;
};

export const AuthPanel = ({
  redirectTo,
  defaults,
  renderHeader,
  fullWidth,
  hideLogo,
  googleOptions = {},
  onAuthenticated,
  onStepChange,
}: Props): JSX.Element => {
  const { t } = useTranslation('auth');
  const { currentUser } = useCurrentUserContext();
  const {
    getAuthAction,
    authActionError,
    isLoading: isLoadingAuthAction,
  } = useAuthAction();
  const [authAction, setAuthAction] = useState<null | AuthAction>(null);
  const { authorizeCalendar = false, googleOnly = false } = googleOptions;
  const isAppleLoginEnabled = useIsAppleLoginEnabled();

  const {
    authenticate,
    isLoading: isAuthenticating,
    result: passwordAuthResult,
    data: passwordAuthData,
    error: authError,
    reset: resetAuthenticate,
  } = useAuthenticate();

  const {
    requestCode,
    verifyCode,
    isVerifying,
    result: codeAuthResult,
    data: codeAuthData,
    error: codeError,
    reset: resetEmailLoginCode,
  } = useEmailLoginCode();

  const {
    setName,
    isPending: isSavingName,
    error: nameError,
  } = useSetInitialName();

  // Held back until the optional name step is done, so consumers are notified
  // once, with the account in its finished state.
  const [isAwaitingName, setIsAwaitingName] = useState(false);

  // Set once the consumer has been notified, and never unset. Every consumer
  // takes the user somewhere from `onAuthenticated` - a redirect, a closing
  // modal - and none of that is instant. Without a state of its own the panel
  // falls back to rendering `authAction`, so finishing the name step brings the
  // code screen back up until the redirect lands.
  const [isComplete, setIsComplete] = useState(false);

  const authResult = passwordAuthResult || codeAuthResult;
  const authData = passwordAuthData || codeAuthData;

  // What the panel is currently showing. Usually the server's AuthAction, but
  // the name step is ours and outranks it.
  const step = isAwaitingName ? NAME_STEP : authAction;

  // `nameError` included because a failed name save leaves the panel sitting on
  // the name step: without it the Continue button just stops doing anything.
  const serverErrors = authError || codeError || authActionError || nameError;

  const {
    register,
    handleSubmit: onSubmit,
    control,
    reset: resetForm,
    formState: { errors },
    setFocus,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      name: defaults?.name || '',
      email: defaults?.email || '',
      password: '',
      code: '',
    },
  });

  // Subscribed rather than read with getValues: the email input is uncontrolled,
  // so typing in it does not re-render, and a getValues read at render time would
  // leave the cooldown keyed to whatever address was in the box on first paint.
  const watchedEmail = useWatch({ control, name: 'email' }) ?? '';
  const currentEmail = watchedEmail.trim();

  const { canResend, timeRemaining, formatTime, markSent } =
    useCodeResendCooldown(currentEmail);

  // Tracked locally: the mutation's own isPending is cleared when moving to the
  // code step resets it, so it cannot be relied on to block a duplicate send.
  const [isSendingCode, setIsSendingCode] = useState(false);

  useEffect(() => {
    if (defaults?.name) {
      setValue('name', defaults.name);
    }
    if (defaults?.email) {
      setValue('email', defaults.email);
    }
  }, [defaults?.name, defaults?.email, setValue]);

  // reset form validation when the authAction changes
  useEffect(() => {
    resetForm(undefined, { keepValues: true });
    resetAuthenticate();
    resetEmailLoginCode();
  }, [authAction, resetForm, resetAuthenticate, resetEmailLoginCode]);

  useEffect(() => {
    if (currentUser && authResult && !isAwaitingName) {
      // Important that we notify consumers of authentication only after react's
      // state has settled and `currentUser` becomes available
      setIsComplete(true);
      onAuthenticated?.(authResult, authData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, authResult, authData, isAwaitingName]);

  useEffect(() => {
    onStepChange?.(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const sendCode = async (email: string) => {
    // The in-flight guard, not the cooldown, is what stops a double click. The
    // cooldown starts only once a code has actually gone out, so a failed send
    // leaves Resend available instead of locking the user out for a minute.
    if (isSendingCode) {
      return;
    }

    setIsSendingCode(true);
    try {
      await requestCode(email);
      markSent();
    } catch {
      // Surfaced to the user through the mutation's error state; all that matters
      // here is that no cooldown was recorded.
    } finally {
      setIsSendingCode(false);
    }
  };

  const discoverAction = async (email: string) => {
    let nextAction = await getAuthAction(email);
    // Apple accounts have a verified email, so while Apple sign-in is switched
    // off on the web they can still get in with an emailed code.
    if (nextAction === AuthAction.LoginApple && !isAppleLoginEnabled) {
      nextAction = AuthAction.LoginEmailCode;
    }
    if (nextAction === AuthAction.LoginEmailCode) {
      void sendCode(email);
    }
    setAuthAction(nextAction);
  };

  const handleSubmit = async (values: FormValues) => {
    const email = values.email.trim();
    if (step === NAME_STEP) {
      setName(values.name.trim(), {
        onSuccess: () => setIsAwaitingName(false),
      });
    } else if (!authAction) {
      await discoverAction(email);
    } else if (authAction === AuthAction.LoginEmailCode) {
      verifyCode(
        { email, code: values.code, name: values.name.trim() || undefined },
        {
          onSuccess: (data) => {
            // Only a brand new account with nothing to greet them by needs the
            // extra step; everyone else goes straight through.
            const { isNewUser, user } = data.verifyEmailLoginCode;
            if (isNewUser && !user.name) {
              setIsAwaitingName(true);
            }
          },
          // A wrong code is retyped from scratch, so clear the boxes rather
          // than make the user delete six digits first.
          onError: () => {
            setValue('code', '');
            setFocus('code');
          },
        }
      );
    } else if (
      authAction === AuthAction.LoginPassword ||
      authAction === AuthAction.Register
    ) {
      authenticate(authAction, { ...values, email });
    } else {
      // An action this client is too old to know about. Report it, then re-run
      // discovery so the submit does something: if the server has since been
      // rolled back, or the address now resolves to a step we do understand, the
      // user continues instead of being stranded on a button that does nothing.
      Sentry.captureException(
        new Error(`Auth form submitted with unsupported action: ${authAction}`)
      );
      await discoverAction(email);
    }
  };

  const isNameStep = step === NAME_STEP;

  const nameControl = (
    <FormControl isInvalid={!!errors.name} variant="floating">
      <Input
        autoComplete="name"
        autoFocus={authAction === AuthAction.Register || isNameStep}
        bg="white"
        id="name"
        placeholder=" " // intentional space for floating labels
        {...register('name', {
          validate: {
            name: (value) => {
              if (authAction !== AuthAction.Register && !isNameStep) {
                return true;
              }
              if (!value.trim().length) {
                return t('validation.required');
              }
            },
          },
        })}
      />
      <FormLabel color="customgray.mid" htmlFor="name">
        {isNameStep ? t('name_your') : t('name')}
      </FormLabel>
      <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
    </FormControl>
  );

  // Shown on every step except Google and Apple, which offer "go back" instead.
  // Stated as an exclusion so a future AuthAction the client does not recognise
  // still renders a usable field rather than an empty panel.
  const emailControl = authAction !== AuthAction.LoginGoogle &&
    authAction !== AuthAction.LoginApple && (
      <FormControl isInvalid={!!errors.email} variant="floating">
        <InputGroup>
          <Input
            _readOnly={{ opacity: 0.4, cursor: 'default' }}
            autoComplete="username"
            bg="white"
            id="email"
            placeholder=" " // intentional space for floating labels
            readOnly={authAction !== null}
            type="email"
            _focusWithin={{
              _readOnly: {
                borderColor: 'gray.300',
                shadow: 'none',
              },
            }}
            {...register('email', {
              required: t('validation.required'),
              validate: {
                email: (value) =>
                  isValidEmail(value) || t('validation.email_invalid'),
              },
            })}
          />
          <FormLabel color="customgray.mid" htmlFor="email">
            {t('email')}
          </FormLabel>
          {authAction !== null && (
            <InputRightElement pt="2">
              <Button
                colorScheme="dark"
                mr="1"
                size="sm"
                variant="ghost"
                onClick={() => setAuthAction(null)}
              >
                {t('edit_email')}
              </Button>
            </InputRightElement>
          )}
        </InputGroup>
        <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
      </FormControl>
    );

  const passwordControl = (
    <FormControl isInvalid={!!errors.password} variant="floating">
      <Controller
        control={control}
        name="password"
        render={({ field }) => (
          <PasswordInput
            autoFocus={authAction === AuthAction.LoginPassword}
            bg="white"
            id="password"
            placeholder={t('password')}
            autoComplete={
              authAction === AuthAction.Register
                ? 'new-password'
                : 'current-password'
            }
            {...field}
          />
        )}
        rules={{
          validate: {
            password: (value) => {
              if (authAction === AuthAction.LoginGoogle) {
                return true;
              }
              if (!value.trim().length) {
                return t('validation.required');
              }
              if (value.length < 8) {
                return t('validation.password_too_short');
              }
            },
          },
        }}
      />
      <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
    </FormControl>
  );

  return (
    <AuthFormStack
      fullWidth={fullWidth}
      hideLogo={hideLogo}
      header={
        <AuthHeaderRenderer
          authAction={authAction}
          email={currentEmail}
          isNameStep={isNameStep}
          renderHeader={renderHeader}
        />
      }
      onSubmit={onSubmit(handleSubmit)}
    >
      {serverErrors && <AuthError authError={serverErrors} />}

      <AnimatePresence mode="popLayout">
        {isComplete ? (
          <MotionContainer align="center" key="complete">
            <Spinner />
          </MotionContainer>
        ) : isNameStep ? (
          <MotionContainer key={NAME_STEP}>
            {nameControl}
            <Button
              data-testid="auth-name-submit"
              isLoading={isSavingName}
              type="submit"
            >
              {t('name_prompt_submit')}
            </Button>
            <Box textAlign="center">
              {/* The account already exists and is logged in by this point, so
                  the name is genuinely optional - leaving the panel is a valid
                  outcome, not an abandoned signup.

                  A Button rather than a Link for the same reason as the resend
                  control: no destination, and an anchor without an href cannot be
                  reached or operated from the keyboard. */}
              <Button
                colorScheme="dark"
                data-testid="auth-name-skip"
                fontSize="sm"
                fontWeight="normal"
                type="button"
                variant="link"
                onClick={() => setIsAwaitingName(false)}
              >
                {t('name_prompt_skip')}
              </Button>
            </Box>
          </MotionContainer>
        ) : !authAction ? (
          <MotionContainer key="default">
            <GoogleSignInButton
              redirectTo={redirectTo}
              type={authorizeCalendar ? 'calendar' : 'login'}
            />
            {!googleOnly && !authorizeCalendar && (
              <AppleSignInButton redirectTo={redirectTo} type="login" />
            )}
            {!googleOnly && (
              <>
                <Flex align="center" my="4" pos="relative">
                  <Divider borderColor="gray.400" />
                  <Text px="4">{t('or')}</Text>
                  <Divider borderColor="gray.400" />
                </Flex>
                {emailControl}
                <Button
                  data-testid="auth-continue"
                  isLoading={isLoadingAuthAction || isSendingCode}
                  type="submit"
                >
                  {t('continue')}
                </Button>
              </>
            )}
            <Terms mt="1" />
          </MotionContainer>
        ) : authAction === AuthAction.Register ? (
          <>
            {emailControl}
            <MotionContainer key={AuthAction.Register}>
              {nameControl}
              {passwordControl}
              <Button
                data-testid="auth-submit"
                isLoading={isAuthenticating}
                type="submit"
              >
                {t('create_account')}
              </Button>
              <Terms />
            </MotionContainer>
          </>
        ) : authAction === AuthAction.LoginGoogle ? (
          <>
            {emailControl}
            <MotionContainer key={AuthAction.LoginGoogle}>
              <GoogleSignInButton
                redirectTo={redirectTo}
                type={authorizeCalendar ? 'calendar' : 'login'}
              />
              <Box color="customgray.mid" fontSize="sm">
                <Trans i18nKey="sign_in_with_google_go_back" t={t}>
                  If you&apos;d like to use a different email address,{' '}
                  <Link
                    colorScheme="dark"
                    onClick={(event) => {
                      event.preventDefault();
                      setAuthAction(null);
                    }}
                  >
                    go back
                  </Link>
                </Trans>
              </Box>
            </MotionContainer>
          </>
        ) : authAction === AuthAction.LoginApple ? (
          <>
            {emailControl}
            <MotionContainer key={AuthAction.LoginApple}>
              <AppleSignInButton redirectTo={redirectTo} type="login" />
              <Box color="customgray.mid" fontSize="sm">
                <Trans i18nKey="sign_in_with_apple_go_back" t={t}>
                  If you&apos;d like to use a different email address,{' '}
                  <Link
                    colorScheme="dark"
                    onClick={(event) => {
                      event.preventDefault();
                      setAuthAction(null);
                    }}
                  >
                    go back
                  </Link>
                </Trans>
              </Box>
            </MotionContainer>
          </>
        ) : authAction === AuthAction.LoginEmailCode ? (
          <>
            {emailControl}
            <MotionContainer key={AuthAction.LoginEmailCode}>
              <EmailCodeStep
                canResend={canResend}
                control={control}
                email={currentEmail}
                errorMessage={errors.code?.message}
                formatTime={formatTime}
                isVerifying={isVerifying}
                timeRemaining={timeRemaining}
                onComplete={() => void onSubmit(handleSubmit)()}
                onResend={() => void sendCode(currentEmail)}
              />
            </MotionContainer>
          </>
        ) : authAction === AuthAction.LoginPassword ? (
          <>
            {emailControl}
            <MotionContainer key={AuthAction.LoginPassword}>
              {passwordControl}
              <Box textAlign="left">
                <ResetPasswordLink fontSize="sm" />
              </Box>
              <Button
                data-testid="auth-submit"
                isLoading={isAuthenticating}
                type="submit"
              >
                {t('sign_in')}
              </Button>
              <Box textAlign="left">
                {/* Doubles as a password reset that never touches their
                    credential: verifyEmailLoginCode leaves Identity alone, so
                    the password keeps working afterwards.

                    A Button rather than a Link: no destination, and an anchor
                    without an href is not keyboard operable. */}
                <Button
                  colorScheme="dark"
                  fontSize="sm"
                  fontWeight="normal"
                  type="button"
                  variant="link"
                  onClick={() => {
                    void sendCode(currentEmail);
                    setAuthAction(AuthAction.LoginEmailCode);
                  }}
                >
                  {t('email_me_a_code')}
                </Button>
              </Box>
            </MotionContainer>
          </>
        ) : (
          <>
            {/* Unknown action - a client older than the server's AuthAction
                enum. Keep the email field and a way to retry rather than
                rendering an empty panel. */}
            {emailControl}
            <MotionContainer key="unsupported">
              {/* Distinct from auth-continue on the first step: both can be in
                  the DOM at once mid-transition, and the e2e helper selects on
                  that id. */}
              <Button
                data-testid="auth-retry"
                isLoading={isLoadingAuthAction}
                type="submit"
              >
                {t('continue')}
              </Button>
            </MotionContainer>
          </>
        )}
      </AnimatePresence>
    </AuthFormStack>
  );
};

const MotionContainer = forwardRef((props: MotionFlexProps, ref) => (
  <MotionFlex
    animate={{ opacity: 1 }}
    direction="column"
    exit={{ opacity: 0 }}
    flex="1"
    gap="3"
    initial={{ opacity: 0 }}
    overflow="hidden"
    ref={ref}
    transition={{ duration: 0.1 }}
    {...props}
  />
));
